import { theme, formatMoney, publish, PUB_SUB_EVENTS } from './global.js';
import { addToCart } from './cart.js';

class ProductForm extends HTMLElement {
  constructor() {
    super();
    const json = this.querySelector('[data-product-json]');
    this.product = json ? JSON.parse(json.textContent) : null;
    this.form = this.querySelector('form');
    this.variantIdInput = this.querySelector('[data-selected-variant-id]');
    this.optionButtons = Array.from(this.querySelectorAll('[data-option-value]'));
    this.selectedOptions = this.getInitialOptions();

    this.optionButtons.forEach((btn) => btn.addEventListener('click', () => this.selectOption(btn)));
    this.form?.addEventListener('submit', (e) => this.onSubmit(e));

    this.updateAvailability();
    // Multi-option products start with nothing chosen: buying is disabled until every option is picked.
    this.needsSelection = this.dataset.needsSelection === 'true' && !this.isComplete();
    if (this.needsSelection) this.setNeedsSelection(true);
  }

  isComplete() {
    return Boolean(this.product) && Object.keys(this.selectedOptions).length >= this.product.options.length;
  }

  setNeedsSelection(on) {
    this.needsSelection = on;
    if (!on) return;
    document.querySelectorAll('[data-add-to-cart-btn], [data-buy-now-btn], [data-sticky-atc-add]').forEach((el) => {
      el.disabled = true;
    });
    const text = document.querySelector('[data-add-to-cart-text]');
    if (text) text.textContent = theme.strings.selectOptions || 'Select options';
    document.querySelectorAll('[data-hide-when-sold-out]').forEach((el) => {
      el.hidden = true;
    });
    this.showFromPrice();
  }

  // Until every option is chosen, a product whose variants cost different amounts shows "From: <lowest price>".
  showFromPrice() {
    if (!this.product?.price_varies) return;
    const priceEl = document.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.textContent = (theme.strings.fromPrice || 'From: __PRICE__').replace('__PRICE__', formatMoney(this.product.price_min, theme.moneyFormat));
    }
    ['[data-product-compare-price]', '[data-product-discount]', '[data-gallery-deal]'].forEach((selector) => {
      const el = document.querySelector(selector);
      if (el) el.hidden = true;
    });
    const stickyPrice = document.querySelector('[data-sticky-atc-price]');
    if (stickyPrice) stickyPrice.textContent = formatMoney(this.product.price_min, theme.moneyFormat);
  }

  getInitialOptions() {
    const groups = {};
    this.optionButtons.forEach((btn) => {
      const pos = btn.dataset.optionPosition;
      if (btn.classList.contains('is-selected')) groups[pos] = btn.dataset.value;
    });
    return groups;
  }

  selectOption(btn) {
    const pos = btn.dataset.optionPosition;
    this.selectedOptions[pos] = btn.dataset.value;

    this.querySelectorAll(`[data-option-position="${pos}"] [data-option-value]`).forEach((b) => {
      b.classList.toggle('is-selected', b === btn);
      b.setAttribute('aria-pressed', b === btn);
    });
    const label = this.querySelector(`.product-form__option[data-option-position="${pos}"] [data-option-selected-value]`);
    if (label) label.textContent = btn.dataset.value;
    const colourLabel = this.querySelector(`.product-form__option[data-option-position="${pos}"] [data-colour-label]`);
    if (colourLabel) colourLabel.textContent = colourLabel.dataset.chosenText.replace('{value}', btn.dataset.value);

    this.updateVariant();
  }

  findMatchedVariant() {
    if (!this.product) return null;
    const values = Object.values(this.selectedOptions);
    return this.product.variants.find((v) =>
      values.every((val, i) => v[`option${i + 1}`] === Object.values(this.selectedOptions)[i])
    ) || this.product.variants.find((v) => [v.option1, v.option2, v.option3].filter(Boolean).every((opt) => values.includes(opt)));
  }

  updateVariant() {
    if (!this.isComplete()) {
      this.setNeedsSelection(true);
      return;
    }
    this.needsSelection = false;
    const variant = this.findMatchedVariant();
    if (!variant) return;

    this.variantIdInput.value = variant.id;
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    publish(PUB_SUB_EVENTS.variantChange, { variant, product: this.product });

    this.updatePrice(variant);
    this.updateAddToCartState(variant);
    document.querySelector('product-gallery')?.goToMediaId?.(variant.featured_media?.id);

    if (variant.id && history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }

    this.updateAvailability(variant);
  }

  // Replays a short "pop" animation on an element (used when the price changes with the variant).
  flash(el) {
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.classList.remove('is-price-changing');
    void el.offsetWidth;
    el.classList.add('is-price-changing');
    const wrap = el.closest('.product-main__price');
    if (wrap) {
      wrap.classList.remove('is-price-refreshing');
      void wrap.offsetWidth;
      wrap.classList.add('is-price-refreshing');
    }
  }

  updatePrice(variant) {
    const priceEl = document.querySelector('[data-product-price]');
    if (!priceEl) return;
    const compareEl = document.querySelector('[data-product-compare-price]');
    const nextPrice = formatMoney(variant.price, theme.moneyFormat);
    const priceChanged = priceEl.textContent.trim() !== nextPrice;
    priceEl.textContent = nextPrice;
    if (priceChanged) this.flash(priceEl);
    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        const nextCompare = formatMoney(variant.compare_at_price, theme.moneyFormat);
        const compareChanged = compareEl.hidden || compareEl.textContent.trim() !== nextCompare;
        compareEl.textContent = nextCompare;
        compareEl.hidden = false;
        if (compareChanged) this.flash(compareEl);
      } else {
        compareEl.hidden = true;
      }
    }
    const dealEl = document.querySelector('[data-gallery-deal]');
    if (dealEl) {
      const onSale = variant.compare_at_price > variant.price;
      dealEl.hidden = !onSale;
      if (onSale) {
        dealEl.textContent = dealEl.dataset.label;
        if (priceChanged) this.flash(dealEl);
      }
    }
    const discountEl = document.querySelector('[data-product-discount]');
    if (discountEl) {
      const onSale = variant.compare_at_price > variant.price;
      discountEl.hidden = !onSale;
      if (onSale) {
        const percent = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
        discountEl.querySelector('[data-product-discount-text]').textContent = discountEl.dataset.template.replace('{percent}', percent);
        if (priceChanged) this.flash(discountEl);
      }
    }
  }

  updateAddToCartState(variant) {
    // The cart buttons live in the buy box column, outside this element, so look them up on the page.
    const btn = document.querySelector('[data-add-to-cart-btn]');
    const buyNowBtn = document.querySelector('[data-buy-now-btn]');
    const text = document.querySelector('[data-add-to-cart-text]');
    const stockPill = document.querySelector('[data-stock-pill]');
    const available = variant && variant.available;
    if (stockPill) {
      stockPill.classList.toggle('is-in', Boolean(available));
      stockPill.classList.toggle('is-out', !available);
      stockPill.textContent = available ? theme.strings.inStock || 'In stock' : theme.strings.soldOut || 'Sold out';
    }
    [btn, buyNowBtn].forEach((el) => {
      if (!el) return;
      el.disabled = !available;
    });
    if (text) text.textContent = available ? theme.strings.addToCart : theme.strings.soldOut;
    // Delivery estimate and the quick WhatsApp questions only make sense while the item can be bought.
    document.querySelectorAll('[data-hide-when-sold-out]').forEach((el) => {
      el.hidden = !available;
    });
  }

  updateAvailability(initialVariant) {
    if (!this.product) return;
    // Grey out / strike through option combinations with no available variant, without hiding them.
    this.optionButtons.forEach((btn) => {
      const pos = Number(btn.dataset.optionPosition) - 1;
      const testOptions = { ...this.selectedOptions, [btn.dataset.optionPosition]: btn.dataset.value };
      const values = Object.values(testOptions);
      const hasAvailable = this.product.variants.some(
        (v) => v[`option${pos + 1}`] === btn.dataset.value && v.available
      );
      btn.dataset.unavailable = String(!hasAvailable);
    });
    if (initialVariant) this.updateAddToCartState(initialVariant);
  }

  onSubmit(e) {
    e.preventDefault();
    if (this.needsSelection) return;
    const formData = new FormData(this.form);
    const isBuyNow = e.submitter?.name === 'buy_now';
    addToCart(formData, { openDrawer: !isBuyNow }).then(({ ok }) => {
      if (ok && isBuyNow) window.location.href = theme.routes.cart || '/cart';
    });
  }
}

customElements.define('product-form', ProductForm);

// `<quantity-stepper>` needs no component class of its own: the increase/
// decrease buttons are handled by the global delegated listener in cart.js,
// which works for any [data-quantity-decrease/increase] pair on the page.
